// DealForm — «Nuevo deal» con la gramática del SO (tarjeta, campos grandes,
// botón con el degradado de la org). POST /admin/deals/ (org del JWT). Si quien
// crea es vendedora, el backend fija el dueño desde su JWT y solo acepta leads
// asignados a ella; aquí el buscador de leads le pide solo los suyos.

import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Briefcase, Loader2, Search, X } from 'lucide-react';

import { listLeads, type LeadDTO } from '@/modules/leads-import';
import { useCreateDeal } from '../hooks/useDeals';
import type { Deal, DealCreateIn, DealStage } from '../types';
import { Field } from './DealSheet';
import { stageStyle } from './parts';

interface DealFormProps {
  stages: DealStage[];
  isSeller?: boolean;
  onCreated?: (deal: Deal) => void;
  onCancel?: () => void;
}

const CURRENCIES = ['MXN', 'USD', 'CAD', 'EUR'];

export function DealForm({ stages, isSeller, onCreated, onCancel }: DealFormProps) {
  const { submit, loading, error } = useCreateDeal();
  const openStages = useMemo(() => stages.filter((s) => s.kind === 'open'), [stages]);
  const [f, setF] = useState({
    title: '', value_amount: '', currency: 'MXN', stage_id: openStages[0]?.id ?? null as number | null,
    probability: '', expected_close_date: '', description: '',
  });
  const [lead, setLead] = useState<LeadDTO | null>(null);

  useEffect(() => {
    if (f.stage_id == null && openStages[0]) setF((x) => ({ ...x, stage_id: openStages[0].id }));
  }, [openStages, f.stage_id]);

  const stage = openStages.find((s) => s.id === f.stage_id);
  const prob = f.probability === '' ? null : Number(f.probability);
  const value = f.value_amount === '' ? 0 : Number(f.value_amount);
  const valid = !!f.title.trim() && !Number.isNaN(value) && value >= 0 && (prob === null || (prob >= 0 && prob <= 100));

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!valid) return;
    const payload: DealCreateIn = {
      title: f.title.trim(),
      description: f.description.trim(),
      value_amount: f.value_amount || '0',
      currency: f.currency,
      expected_close_date: f.expected_close_date || null,
      ...(f.stage_id != null ? { stage_id: f.stage_id } : {}),
      ...(prob !== null ? { probability: Math.round(prob) } : {}),
      ...(lead ? { lead_id: lead.id } : {}),
    };
    const created = await submit(payload);
    if (created) onCreated?.(created);
  }

  return (
    <form onSubmit={handleSubmit} className="mx-auto w-full max-w-2xl p-3 sm:p-6" aria-labelledby="dl-new-title">
      <div className="orion-card overflow-hidden rounded-[20px] border border-border bg-card">
        <header className="flex items-center gap-3 border-b border-border px-5 py-4">
          <span className="dl-accent-soft dl-accent-text grid h-10 w-10 place-items-center rounded-xl"><Briefcase className="h-5 w-5" aria-hidden /></span>
          <div className="min-w-0 flex-1">
            <h2 id="dl-new-title" className="text-[16px] font-semibold text-foreground">Nuevo deal</h2>
            <p className="text-[12.5px] text-muted-foreground">Con el título basta; lo demás se puede completar después en la ficha.</p>
          </div>
        </header>

        <div className="space-y-4 px-5 py-5">
          <Field label="Título *">
            <input required autoFocus className="dl-input" value={f.title} placeholder="Viaje escolar · 38 alumnos · marzo"
              onChange={(e) => setF({ ...f, title: e.target.value })} />
          </Field>

          <LeadPicker value={lead} onChange={setLead} mine={!!isSeller} />

          <div className="grid gap-4 min-[420px]:grid-cols-[1fr,120px]">
            <Field label="Valor">
              <input className="dl-input tabular-nums" type="number" inputMode="decimal" min={0} step="0.01" placeholder="0"
                value={f.value_amount} onChange={(e) => setF({ ...f, value_amount: e.target.value })} />
            </Field>
            <Field label="Moneda">
              <select className="dl-input" value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value })}>
                {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
          </div>

          {openStages.length > 0 && (
            <fieldset>
              <legend className="mb-1 block text-[12.5px] font-medium text-foreground">Etapa inicial</legend>
              <div role="radiogroup" aria-label="Etapa inicial" className="grid grid-cols-2 gap-1 rounded-xl border border-border bg-muted p-1 min-[480px]:grid-cols-4">
                {openStages.map((s) => {
                  const sel = s.id === f.stage_id;
                  return (
                    <button key={s.id} type="button" role="radio" aria-checked={sel} style={stageStyle(s)}
                      onClick={() => setF({ ...f, stage_id: s.id })}
                      className={`flex min-h-[44px] min-w-0 items-center gap-1.5 rounded-[10px] px-2 text-[13px] font-medium ${sel ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
                      <span className="dl-dot h-2 w-2 shrink-0 rounded-full" aria-hidden /><span className="truncate">{s.name}</span>
                    </button>
                  );
                })}
              </div>
            </fieldset>
          )}

          <div className="grid gap-4 min-[420px]:grid-cols-2">
            <Field label="Probabilidad %" hint={stage ? `Si lo dejas vacío: ${stage.default_probability}% (de la etapa)` : undefined}>
              <input className="dl-input tabular-nums" type="number" inputMode="numeric" min={0} max={100} placeholder={stage ? String(stage.default_probability) : ''}
                value={f.probability} onChange={(e) => setF({ ...f, probability: e.target.value })} />
            </Field>
            <Field label="Cierre esperado">
              <input className="dl-input" type="date" value={f.expected_close_date} onChange={(e) => setF({ ...f, expected_close_date: e.target.value })} />
            </Field>
          </div>

          <Field label="Notas">
            <textarea className="dl-input" rows={3} value={f.description} placeholder="Contexto, requisitos, quién decide…"
              onChange={(e) => setF({ ...f, description: e.target.value })} />
          </Field>

          {error && <p role="alert" className="rounded-lg bg-destructive/10 px-3 py-2 text-[12.5px] text-destructive">{error}</p>}
        </div>

        <footer className="flex flex-col-reverse gap-2 border-t border-border px-5 py-4 min-[420px]:flex-row min-[420px]:justify-end">
          {onCancel && <button type="button" onClick={onCancel} className="h-11 rounded-xl px-4 text-[14px] font-medium text-foreground hover:bg-muted">Cancelar</button>}
          <button type="submit" disabled={!valid || loading} className="dl-btn-brand inline-flex h-11 items-center justify-center gap-1.5 rounded-xl px-5 text-[14px] font-semibold">
            {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}Crear deal
          </button>
        </footer>
      </div>
    </form>
  );
}

/** Buscador de leads (opcional). Si el rol no puede leer leads, se explica. */
function LeadPicker({ value, onChange, mine }: { value: LeadDTO | null; onChange: (l: LeadDTO | null) => void; mine: boolean }) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<LeadDTO[] | null>(null);
  const [state, setState] = useState<'idle' | 'loading' | 'denied' | 'error'>('idle');

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setRows(null); setState('idle'); return; }
    let alive = true;
    setState('loading');
    const t = window.setTimeout(() => {
      listLeads({ search: term, limit: 8, ...(mine ? { mine: true } : {}) })
        .then((r) => { if (alive) { setRows(r); setState('idle'); } })
        .catch((e: any) => { if (alive) { setRows(null); setState(e?.status === 403 ? 'denied' : 'error'); } });
    }, 250);
    return () => { alive = false; window.clearTimeout(t); };
  }, [q, mine]);

  if (value) {
    return (
      <div>
        <span className="mb-1 block text-[12.5px] font-medium text-foreground">Cliente (lead)</span>
        <div className="flex items-center gap-2 rounded-[10px] border border-border bg-background px-3 py-2">
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[14px] font-medium text-foreground">{value.name || value.email || value.phone}</span>
            <span className="block truncate text-[12px] text-muted-foreground">{[value.phone, value.email].filter(Boolean).join(' · ') || 'Sin contacto'}</span>
          </span>
          <button type="button" onClick={() => onChange(null)} aria-label="Quitar lead" className="grid h-9 w-9 place-items-center rounded-lg text-muted-foreground hover:bg-muted"><X className="h-4 w-4" /></button>
        </div>
      </div>
    );
  }
  return (
    <div>
      <Field label="Cliente (lead) · opcional" hint={state === 'denied' ? 'Tu rol no puede buscar leads aquí; puedes ligarlo después.' : 'Busca por nombre, teléfono o correo.'}>
        <span className="relative block">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
          <input className="dl-input" style={{ paddingLeft: '2.25rem' }} value={q} onChange={(e) => setQ(e.target.value)} placeholder="María López"
            disabled={state === 'denied'} aria-autocomplete="list" aria-controls="dl-lead-results" />
        </span>
      </Field>
      {state === 'loading' && <p className="mt-1 flex items-center gap-1.5 text-[12px] text-muted-foreground" role="status"><Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />Buscando…</p>}
      {state === 'error' && <p className="mt-1 text-[12px] text-destructive">No se pudo buscar leads.</p>}
      {rows && (
        <ul id="dl-lead-results" className="mt-1 max-h-56 overflow-y-auto rounded-xl border border-border bg-popover p-1">
          {rows.length === 0 ? <li className="px-3 py-2 text-[12.5px] text-muted-foreground">Sin coincidencias.</li> : rows.map((l) => (
            <li key={l.id}>
              <button type="button" onClick={() => { onChange(l); setQ(''); setRows(null); }}
                className="flex min-h-[44px] w-full flex-col items-start rounded-lg px-3 py-1.5 text-left hover:bg-muted">
                <span className="text-[13.5px] font-medium text-foreground">{l.name || l.email || l.phone}</span>
                <span className="text-[12px] text-muted-foreground">{[l.phone, l.email].filter(Boolean).join(' · ')}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
